import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Alert,
} from '@mui/material'
import {
  Mic,
  Stop,
  FiberManualRecord,
  CheckCircle,
  Edit,
  Star,
  StarBorder,
  Cancel,
} from '@mui/icons-material'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createEventsApi,
  createNotesApi,
  fetchActivityCategories,
  fetchObservationCategories,
} from '../../HTTPS/ApiService'
import {
  convertLocalDateTimeToUTC,
  convertLocalDateToUTC,
  getCurrentLocalDate,
  getCurrentLocalTime,
} from '../../utils/chartUtils'
import { usePatient } from '../../context/PatientContext'
import { Sparkles } from 'lucide-react'
import { useToastContext } from '../../context/ToastContext'

const QuickJournalingVoiceInputComp = ({
  setQuickJournalingVoiceInputOpen,
  fetchEvents,
  setVoiceInputError,
  onClose,
}) => {
  const { setOpenSuccessToast, setToastMessage } = useToastContext()
  const queryClient = useQueryClient()
  const { patient } = usePatient()

  // Recording state machine: 'idle' -> 'preparing' -> 'recording' -> 'processing' -> 'completed'
  const [recordingStage, setRecordingStage] = useState('idle')
  const [elapsed, setElapsed] = useState(0)
  const [tableData, setTableData] = useState([])
  const [selectedRow, setSelectedRow] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [hasClassified, setHasClassified] = useState(false)

  const timerRef = useRef(null)
  const hasStartedRecordingRef = useRef(false)
  const classificationAttemptedRef = useRef(false)

  const { recording, transcript, startRecording, stopRecording, cancelRecording } =
    useAudioRecorder('audio/webm', patient?.id, true)

  // Expose cancelRecording to parent via onClose callback - REQUIRED FOR THE CLOSE BUTTON IN POP UP
  useEffect(() => {
    if (onClose) {
      onClose(() => {
        if (recording) {
          if (timerRef.current) clearInterval(timerRef.current)
          cancelRecording()
        }
      })
    }
  }, [recording, onClose, cancelRecording])

  const { data: observationCategories } = useQuery({
    queryKey: ['observationCategories'],
    queryFn: fetchObservationCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })

  const { data: activityCategories } = useQuery({
    queryKey: ['activityCategories'],
    queryFn: fetchActivityCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })

  /**
   * Classify API mutation
   * 
   * Expected API Response Structure:
   * {
   *   "success": true,
   *   "data": {
   *     "observations": {
   *       "Mood & Behavior": "text",
   *       "Health Events": "text",
   *       "Sleep & Rest": "text",
   *       ...
   *     },
   *     "activities": {
   *       "Meals": "text",
   *       "Medication": "text",
   *       "Entertainment": "text",
   *       ...
   *     },
   *     "timestamp": "2025-10-19T12:34:56.789Z"
   *   }
   * }
   * 
   * Error Types:
   * - VALIDATION_ERROR (400): "Invalid input, enter a valid observation or activity"
   * - EMPTY_TEXT_ERROR (400): "No text provided or text is empty"
   * - Network/Server errors: Other error types
   */
  const classifyMutation = useMutation({
    mutationFn: async (text) => {
      const res = await fetch(
        `${process.env.REACT_APP_MK_AGENT_API}/classify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        }
      )
      const data = await res.json()
      if (!res.ok || !data.success) {
        // Enhanced error handling for new API error types
        const errorMessage = data.error?.message || data.message || 'Classification failed'
        const error = new Error(errorMessage)

        // Differentiate error types based on API response
        if (res.status === 400) {
          if (errorMessage.toLowerCase().includes('no text provided') ||
            errorMessage.toLowerCase().includes('text is empty')) {
            error.type = 'EMPTY_TEXT_ERROR'
          } else if (errorMessage.toLowerCase().includes('invalid input') ||
            errorMessage.toLowerCase().includes('valid observation or activity')) {
            error.type = 'VALIDATION_ERROR'
          }
        }

        error.message = errorMessage
        throw error
      }
      return data.data
    },
  })

  const saveObservationsMutation = useMutation({
    mutationFn: async (observations) => {
      const formattedObservations = observations.map((obs) => ({
        ...obs,
        ntDate: convertLocalDateToUTC(obs.ntDate, obs.ntTime),
        ntTime: convertLocalDateTimeToUTC(obs.ntTime),
      }))
      return await createNotesApi(formattedObservations)
    },
  })

  const saveActivitiesMutation = useMutation({
    mutationFn: async (activities) => {
      const formattedActivities = activities.map((act) => ({
        ...act,
        evDate: convertLocalDateToUTC(act.evDate, act.evTime),
        evTime: convertLocalDateTimeToUTC(act.evTime),
      }))
      return await createEventsApi(formattedActivities)
    },
  })

  // Auto-start recording on mount
  useEffect(() => {
    if (hasStartedRecordingRef.current) return
    hasStartedRecordingRef.current = true

    setRecordingStage('preparing')

    const startTimeout = setTimeout(() => {
      startRecording()
      setRecordingStage('recording')

      // Start 30-second timer
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          if (prev + 1 >= 30) {
            clearInterval(timerRef.current)
            stopRecording()
            setRecordingStage('processing')
            return 30
          }
          return prev + 1
        })
      }, 1000)
    }, 1500)

    return () => {
      clearTimeout(startTimeout)
      if (timerRef.current) clearInterval(timerRef.current)
      stopRecording()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle classification after recording stops
  useEffect(() => {
  if (recordingStage !== 'processing' || classificationAttemptedRef.current || hasClassified) {
    return
  }

  // Only proceed if we have a transcript
  if (!transcript) {
    return // Wait for transcript to be populated
  }

  console.log('Raw transcript from API:', transcript)
  console.log('Transcript length:', transcript?.length)

  if (transcript.length > 0) {
    classificationAttemptedRef.current = true
    console.log('Sending to classify API:', transcript)
    classifyMutation.mutate(transcript)
  } else {
    // Transcript exists but is empty string
    classificationAttemptedRef.current = true
    if (setVoiceInputError) {
      setVoiceInputError({ type: 'error', message: 'No speech detected. Please try again.' })
    }
    setRecordingStage('completed')
    
    // Close dialog after showing error
    setTimeout(() => {
      if (setQuickJournalingVoiceInputOpen) setQuickJournalingVoiceInputOpen(false)
    }, 3000)
  }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingStage, transcript, hasClassified])

  // Handle classification success
  useEffect(() => {
    if (!classifyMutation.data || hasClassified) return

    setHasClassified(true)
    const newData = []

    // Log API response timestamp for debugging/audit purposes
    if (classifyMutation.data.timestamp) {
      console.log('Classification timestamp:', classifyMutation.data.timestamp)
    }

    // Process observations from new API structure
    Object.entries(classifyMutation.data.observations || {}).forEach(
      ([category, desc]) => {
        // Skip empty descriptions
        if (!desc || desc.trim().length === 0) return

        newData.push({
          selectedType: 'Observation',
          text: desc,
          ntDate: getCurrentLocalDate(),
          ntTime: getCurrentLocalTime(),
          isImp: false,
          recipientId: patient?.id,
          file: null,
          type: observationCategories?.find((cat) => cat.text === category)?.id || 0,
          typeName: category,
        })
      }
    )

    // Process activities from new API structure
    Object.entries(classifyMutation.data.activities || {}).forEach(
      ([category, desc]) => {
        // Skip empty descriptions
        if (!desc || desc.trim().length === 0) return

        newData.push({
          selectedType: 'Activity',
          text: desc,
          evDate: getCurrentLocalDate(),
          evTime: getCurrentLocalTime(),
          isImp: false,
          recipientId: patient?.id,
          file: null,
          type: activityCategories?.find((cat) => cat.text === category)?.id || 0,
          typeName: category,
        })
      }
    )

    setTableData(newData)
    setRecordingStage('completed')
  }, [classifyMutation.data, observationCategories, activityCategories, hasClassified, patient?.id])

  // Handle classification error with enhanced error type differentiation
  useEffect(() => {
    if (classifyMutation.isError && !hasClassified) {
      setHasClassified(true)
      const error = classifyMutation.error

      console.log('Classification error:', error)
      console.log('Error type:', error?.type)
      console.log('Error message:', error?.message)

      // Handle validation errors (invalid/gibberish input)
      if (error?.type === 'VALIDATION_ERROR') {
        if (setVoiceInputError) {
          setVoiceInputError({
            type: 'error',
            message: error.message || 'Invalid input, enter a valid observation or activity'
          })
        }
        setRecordingStage('completed')

        // Close dialog for validation errors - no fallback entry
        setTimeout(() => {
          if (setQuickJournalingVoiceInputOpen) setQuickJournalingVoiceInputOpen(false)
        }, 500) // Decreased to 500ms to instant close popup - DO NOT remove timeout
        return
      }

      // Handle empty text errors
      if (error?.type === 'EMPTY_TEXT_ERROR') {
        if (setVoiceInputError) {
          setVoiceInputError({
            type: 'error',
            message: 'No text provided or text is empty. Please try again.'
          })
        }
        setRecordingStage('completed')

        setTimeout(() => {
          if (setQuickJournalingVoiceInputOpen) setQuickJournalingVoiceInputOpen(false)
        }, 3000)
        return
      }

      // Handle network/server errors or other classification failures
      // Create fallback "Other Categories" entry for non-validation errors
      const fallbackEntry = {
        selectedType: 'Observation',
        text: transcript,
        ntDate: getCurrentLocalDate(),
        ntTime: getCurrentLocalTime(),
        isImp: false,
        recipientId: patient?.id,
        file: null,
        type: 0,
        typeName: 'Other Categories',
        isShortSpeechError: false, // Not a short speech error, but a classification failure
        classificationError: true, // Flag to indicate classification failed
      }

      setTableData([fallbackEntry])
      setRecordingStage('completed')

      // Show warning message for network/server errors
      if (setVoiceInputError) {
        setVoiceInputError({
          type: 'warning',
          message: 'Classification failed. Entry saved as "Other Categories" for manual review.'
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classifyMutation.isError, hasClassified])

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0')
    const s = (sec % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  const getStatusMessage = () => {
    switch (recordingStage) {
      case 'preparing':
        return 'Initializing voice capture...'
      case 'recording':
        return 'Listening... Speak now'
      case 'processing':
        return 'Analyzing and categorizing your input...'
      case 'completed':
        return tableData.length > 0
          ? 'Analysis complete! Review your entries below'
          : 'Ready to close'
      default:
        return ''
    }
  }
  useEffect(() => {
    console.log(tableData)

  }, [tableData])


  const handleSaveAllToJournal = async () => {
    if (tableData.length === 0) {
      if (setVoiceInputError) {
        setVoiceInputError({ type: 'warning', message: 'No entries to save' })
      }
      return
    }

    // Check for unclassified entries (type === 0)
    const unclassifiedEntries = tableData.filter((entry) => entry.type === 0)
    if (unclassifiedEntries.length > 0) {
      if (setVoiceInputError) {
        setVoiceInputError({
          type: 'warning',
          message: `${unclassifiedEntries.length} ${unclassifiedEntries.length === 1 ? 'entry' : 'entries'
            } not classified properly. Please update the category manually.`
        })
      }
      return
    }

    setIsSaving(true)

    try {
      const observations = tableData.filter((entry) => entry.selectedType === 'Observation')
      const activities = tableData.filter((entry) => entry.selectedType === 'Activity')

      const promises = []
      if (observations.length > 0) {
        promises.push(saveObservationsMutation.mutateAsync(observations))
      }
      if (activities.length > 0) {
        promises.push(saveActivitiesMutation.mutateAsync(activities))
      }

      await Promise.all(promises)

      queryClient.invalidateQueries(['Observation', patient?.id])
      if (fetchEvents && typeof fetchEvents === 'function') {
        await fetchEvents()
      }

      setToastMessage(`Successfully added ${tableData.length} entries to journal!`)
      setOpenSuccessToast(true)

      setTimeout(() => {
        if (setQuickJournalingVoiceInputOpen) {
          setQuickJournalingVoiceInputOpen(false)
        }
      }, 1500)
    } catch (error) {
      console.error('Error saving entries:', error)
      if (setVoiceInputError) {
        setVoiceInputError({
          type: 'error',
          message: 'Failed to save entries to journal. Please try again.'
        })
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Use pure transcript from API
  const cleanedTranscript = transcript

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        p: { xs: 1, sm: 2 },
        pb: { xs: tableData.length > 0 ? 10 : 1, sm: 2 },
      }}
    >
      {/* Header */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: '#1D3E7D',
          mb: { xs: 1, sm: 2 },
          textAlign: 'center',
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
        }}
      >
        Voice Journal Entry
      </Typography>

      {/* Recording Card */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          mb: 2,
          border: '2px solid #1D3E7D',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.5, sm: 1 },
              py: { xs: 1, sm: 1.5 },
              px: { xs: 1, sm: 2 },
              bgcolor: '#1D3E7D',
              borderBottom: '2px solid #ED7117',
            }}
          >
            <Mic sx={{ color: 'white', fontSize: { xs: 18, sm: 22 } }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'white',
                fontSize: { xs: '0.85rem', sm: '1rem' },
              }}
            >
              {getStatusMessage()}
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.paper' }}>
            {/* Status Indicator */}
            {(recordingStage === 'preparing' || recordingStage === 'processing') && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'rgba(29, 62, 125, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#ED7117',
                    },
                  }}
                />
              </Box>
            )}

            {/* Recording Animation */}
            {recordingStage === 'recording' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.5 },
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 60, sm: 70 },
                    height: { xs: 60, sm: 70 },
                    borderRadius: '50%',
                    bgcolor: '#ED7117',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                        opacity: 0.8,
                      },
                    },
                  }}
                >
                  <FiberManualRecord
                    sx={{ color: 'white', fontSize: { xs: 28, sm: 32 } }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'error.main',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  {formatTime(elapsed)} / 00:30
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(elapsed / 30) * 100}
                  sx={{
                    width: '100%',
                    maxWidth: { xs: 200, sm: 250 },
                    height: { xs: 4, sm: 5 },
                    borderRadius: 2,
                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'error.main',
                    },
                  }}
                />
              </Box>
            )}

            {/* Transcript Display */}
            {(recordingStage === 'processing' || recordingStage === 'completed') &&
              cleanedTranscript &&
              cleanedTranscript.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 0.5,
                      fontWeight: 700,
                      color: '#1D3E7D',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.85rem',
                    }}
                  >
                    <CheckCircle sx={{ color: '#10b981', fontSize: 16 }} />
                    Transcribed Text
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.6,
                      fontSize: '0.8rem',
                      p: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 1,
                      border: '1px solid #1D3E7D',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {cleanedTranscript}
                  </Typography>
                </Box>
              )}

            {/* Stop and Cancel Recording Buttons */}
            {recording && recordingStage === 'recording' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Tooltip title="Stop Recording Early">
                  <Button
                    variant="contained"
                    size="medium"
                    color="error"
                    startIcon={<Stop sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                    onClick={() => {
                      if (timerRef.current) clearInterval(timerRef.current)
                      stopRecording()
                      setRecordingStage('processing')
                    }}
                    sx={{
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.5, sm: 0.75 },
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    }}
                  >
                    Stop Recording
                  </Button>
                </Tooltip>
                <Tooltip title="Cancel Recording">
                  <Button
                    variant="outlined"
                    size="medium"
                    color="error"
                    startIcon={<Cancel sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                    onClick={() => {
                      if (timerRef.current) clearInterval(timerRef.current)
                      cancelRecording()
                      setRecordingStage('idle')
                      if (setQuickJournalingVoiceInputOpen) {
                        setQuickJournalingVoiceInputOpen(false)
                      }
                    }}
                    sx={{
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.5, sm: 0.75 },
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    }}
                  >
                    Cancel
                  </Button>
                </Tooltip>
              </Box>
            )}

            {/* Success Message */}
            {recordingStage === 'completed' && tableData.length > 0 && (
              <Alert
                icon={<CheckCircle />}
                severity="success"
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Successfully categorized {tableData.length} entries from your
                voice input!
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Results Table */}
      {tableData.length > 0 && (
        <Card elevation={2} sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1, sm: 2 },
                bgcolor: 'primary.main',
                color: 'white',
              }}
            >
              <Sparkles sx={{ fontSize: { xs: 16, sm: 18 } }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                }}
              >
                Categorized Entries ({tableData.length})
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 2 },
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 2 },
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 2 },
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 2 },
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row, i) => (
                    <TableRow
                      key={i}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell
                        sx={{ py: { xs: 0.5, sm: 1 }, px: { xs: 0.5, sm: 2 } }}
                      >
                        <Chip
                          label={row.selectedType}
                          size="small"
                          color={
                            row.selectedType === 'Observation'
                              ? 'primary'
                              : 'secondary'
                          }
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            height: { xs: 20, sm: 22 },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', sm: '0.85rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 2 },
                        }}
                      >
                        {row.typeName}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: { xs: 150, sm: 300 },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 2 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.5,
                            fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          }}
                        >
                          {row.text}
                        </Typography>
                        {row.type === 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'error.main',
                              fontWeight: 600,
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            {row.isShortSpeechError
                              ? '⚠️ Speech too short to categorize'
                              : row.classificationError
                                ? '⚠️ Classification failed - please update category'
                                : '⚠️ Not classified - update category'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ py: { xs: 0.5, sm: 1 }, px: { xs: 0.5, sm: 2 } }}
                      >
                        <Tooltip title="Edit Entry">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setSelectedRow({ ...row, index: i })}
                          >
                            <Edit sx={{ fontSize: { xs: 16, sm: 18 } }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Add to Journal Button */}
      {tableData.length > 0 && (
        <Box
          sx={{
            position: { xs: 'sticky', sm: 'static' },
            bottom: { xs: 0, sm: 'auto' },
            left: 0,
            right: 0,
            zIndex: 1000,
            bgcolor: { xs: 'background.paper', sm: 'transparent' },
            borderTop: { xs: '1px solid #e5e7eb', sm: 'none' },
            p: { xs: 2, sm: 0 },
            mb: { xs: 0, sm: 2 },
            display: 'flex',
            justifyContent: 'center',
            boxShadow: { xs: '0 -2px 10px rgba(0,0,0,0.1)', sm: 'none' },
          }}
        >
          <Button
            variant="contained"
            size="medium"
            onClick={handleSaveAllToJournal}
            disabled={isSaving || tableData.some((entry) => entry.type === 0)}
            startIcon={
              <CheckCircle sx={{ fontSize: { xs: 18, sm: 20 } }} />
            }
            fullWidth
            sx={{
              bgcolor: '#ED7117',
              px: { xs: 2, sm: 4 },
              py: { xs: 1.25, sm: 1.5 },
              borderRadius: 2,
              fontWeight: 700,
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              boxShadow: '0 3px 10px 0 rgba(237, 113, 23, 0.35)',
              maxWidth: { xs: '100%', sm: 'auto' },
              '&:hover': {
                bgcolor: '#d36010',
                boxShadow: '0 4px 14px rgba(237, 113, 23, 0.45)',
              },
              '&:disabled': {
                bgcolor: '#9ca3af',
              },
            }}
          >
            {isSaving
              ? `Saving ${tableData.length} Entries...`
              : `Add ${tableData.length} ${tableData.length === 1 ? 'Entry' : 'Entries'
              } to Journal`}
          </Button>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1D3E7D',
            color: 'white',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Edit /> Edit Entry
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {selectedRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Important Marker */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: selectedRow.isImp ? 'warning.lighter' : 'grey.50',
                  border: '2px solid',
                  borderColor: selectedRow.isImp ? 'warning.main' : 'grey.300',
                  transition: 'all 0.3s ease',
                }}
                onClick={() =>
                  setSelectedRow({ ...selectedRow, isImp: !selectedRow.isImp })
                }
              >
                <IconButton size="small" sx={{ color: 'warning.main' }}>
                  {selectedRow.isImp ? <Star /> : <StarBorder />}
                </IconButton>
                <Typography sx={{ fontWeight: 600 }}>
                  Mark as Important
                </Typography>
              </Box>

              <Divider />

              {/* Date and Time */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Date"
                  type="date"
                  fullWidth
                  value={
                    selectedRow.selectedType === 'Observation'
                      ? selectedRow.ntDate
                      : selectedRow.evDate || ''
                  }
                  onChange={(e) =>
                    setSelectedRow({
                      ...selectedRow,
                      [selectedRow.selectedType === 'Observation'
                        ? 'ntDate'
                        : 'evDate']: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: new Date().toISOString().split('T')[0] }}
                />
                <TextField
                  label="Time"
                  type="time"
                  fullWidth
                  value={
                    selectedRow.selectedType === 'Observation'
                      ? selectedRow.ntTime
                      : selectedRow.evTime || ''
                  }
                  onChange={(e) =>
                    setSelectedRow({
                      ...selectedRow,
                      [selectedRow.selectedType === 'Observation'
                        ? 'ntTime'
                        : 'evTime']: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* Category Selection */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, fontWeight: 600 }}
                >
                  Select Category
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {(selectedRow.selectedType === 'Observation'
                    ? observationCategories
                    : activityCategories
                  )?.map((item) => (
                    <Chip
                      key={item.id}
                      label={item.text}
                      onClick={() =>
                        setSelectedRow({
                          ...selectedRow,
                          type: item.id,
                          typeName: item.text,
                        })
                      }
                      color={
                        selectedRow.type === item.id ? 'primary' : 'default'
                      }
                      variant={
                        selectedRow.type === item.id ? 'filled' : 'outlined'
                      }
                      sx={{
                        fontWeight: selectedRow.type === item.id ? 700 : 500,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor:
                            selectedRow.type === item.id
                              ? 'primary.dark'
                              : 'action.hover',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Description */}
              <TextField
                label="Description"
                multiline
                rows={4}
                fullWidth
                value={selectedRow.text || ''}
                onChange={(e) =>
                  setSelectedRow({ ...selectedRow, text: e.target.value })
                }
                placeholder="Enter description..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setSelectedRow(null)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setTableData((prev) =>
                prev.map((item, idx) =>
                  idx === selectedRow.index ? selectedRow : item
                )
              )
              setSelectedRow(null)
            }}
            variant="contained"
            sx={{
              bgcolor: '#ED7117',
              borderRadius: 2,
              px: 3,
              '&:hover': {
                bgcolor: '#d36010',
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default QuickJournalingVoiceInputComp
